/**
 * Simple @clack-based agent REPL.
 * No React, no ink, no alt-screen — just a scrolling terminal.
 *
 * Design: clack's text() blocks until user submits, so agent output
 * flows naturally between prompts — no readline interleaving issues.
 * Loop: await text() → send → await idle → loop
 */

import type { Dirent } from "node:fs";
import { existsSync, readdirSync, readFileSync, statSync } from "node:fs";
import { basename, dirname, join, parse, resolve } from "node:path";
import * as p from "@clack/prompts";
import { loadConnectorDeclarations } from "@skaile/workspaces/connectors";
import { resolveAgentDir, resolveSettings } from "@skaile/workspaces/core";
import { createAgentSession } from "@skaile/workspaces/runner";
import pc from "picocolors";
import { createReplSession, createTelemetrySink } from "./repl-session.js";
import type { TelemetryLogEntry } from "./telemetry.js";
import { TuiTelemetryProvider } from "./telemetry.js";

function findAiAssetsRoot(startDir: string): string | undefined {
  let current = resolve(startDir);
  const root = parse(current).root;
  while (current !== root) {
    const candidate = join(current, "ai-assets");
    try {
      if (statSync(candidate).isDirectory()) return candidate;
    } catch {}
    const parent = dirname(current);
    if (parent === current) break;
    current = parent;
  }
  return undefined;
}

function countSkills(projectDir: string): number {
  const aiAssetsRoot = findAiAssetsRoot(projectDir);
  if (!aiAssetsRoot) return 0;
  let count = 0;
  const walk = (dir: string) => {
    let entries: Dirent[];
    try {
      entries = readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    if (entries.some((e) => e.isFile() && e.name === "SKILL.md")) {
      count++;
      return;
    }
    for (const e of entries) {
      if (e.isDirectory()) walk(join(dir, e.name));
    }
  };
  walk(aiAssetsRoot);
  return count;
}

/**
 * Options for {@link startRepl}.
 *
 * @param projectDir - Absolute path to the project root. Used to locate the agent dir, settings, and skill count.
 * @param cwd - Working directory passed to the agent process. Defaults to `projectDir`.
 * @param driver - Agent driver override (`"omp"` or `"claude-sdk"`). Falls back to settings resolution.
 * @param model - Model name passed to the agent driver.
 * @param provider - Provider name passed to the agent driver.
 * @param resume - When `true`, load the last session ID from `.skaile/last_session` and resume it.
 * @param resumeSessionId - Explicit session ID to resume. Takes precedence over `resume`.
 * @param verbose - When `true`, show telemetry log lines inline between prompts. Default: `false`.
 *
 * @docLink packages/tui/concepts#start-repl
 */
export interface ReplOptions {
  projectDir: string;
  cwd?: string;
  driver?: string;
  model?: string;
  provider?: string;
  resume?: boolean;
  resumeSessionId?: string;
  /** When true, show telemetry log lines inline. Default: false. */
  verbose?: boolean;
}

/**
 * Inject the ephemeral OMP workspace extension when the resolved driver is `omp`.
 *
 * Writes `.omp/extensions/skaile.ts` under {@link agentCwd} and returns a cleanup
 * callback that removes it. For non-`omp` drivers this is a no-op (returns `undefined`).
 */
async function injectOmpExtension(
  resolvedDriver: string,
  projectDir: string,
  agentCwd: string,
): Promise<(() => void) | undefined> {
  if (resolvedDriver !== "omp") return undefined;

  const fsAsync = await import("node:fs/promises");
  const pathMod = await import("node:path");
  const { WorkspacePlugin } = await import("@skaile/workspaces/workspace-plugin");

  const plugin = new WorkspacePlugin({ projectDir });
  const src = await plugin.buildOmpExtensionSource();
  const extPath = pathMod.default.join(agentCwd, ".omp", "extensions", "skaile.ts");
  await fsAsync.default.mkdir(pathMod.default.dirname(extPath), { recursive: true });
  await fsAsync.default.writeFile(extPath, src);
  return () => {
    fsAsync.default.unlink(extPath).catch(() => {});
  };
}

/**
 * Resolve the session ID to resume, honoring the explicit `resumeSessionId` first
 * and otherwise reading `.skaile/last_session` when `resume` is set.
 */
function resolveResumeSessionId(projectDir: string, opts: ReplOptions): string | undefined {
  let resumeSessionId = opts.resumeSessionId;
  if (!resumeSessionId && opts.resume) {
    const sessionFile = join(projectDir, ".skaile", "last_session");
    try {
      if (existsSync(sessionFile)) {
        resumeSessionId = readFileSync(sessionFile, "utf-8").trim() || undefined;
      }
    } catch {}
  }
  return resumeSessionId;
}

/** Print the REPL intro banner with project, agent, and resume metadata. */
function printBanner(args: {
  projectDir: string;
  displayDriver: string;
  displayModel: string;
  agentDir: string | undefined;
  resumeSessionId: string | undefined;
}): void {
  const { projectDir, displayDriver, displayModel, agentDir, resumeSessionId } = args;

  const skillCount = countSkills(projectDir);
  let connectorCount = 0;
  try {
    connectorCount = loadConnectorDeclarations(projectDir).length;
  } catch {}
  const agentName = agentDir ? basename(agentDir) : "(none)";

  p.intro(`${pc.bold("skaile repl")} ${pc.dim(`— ${displayDriver} / ${displayModel}`)}`);
  p.log.info(pc.dim(`Project: ${projectDir}`));
  p.log.info(pc.dim(`Agent: ${agentName} · ${skillCount} skills · ${connectorCount} connectors`));
  if (resumeSessionId) p.log.info(pc.dim(`Resumed: ${resumeSessionId.slice(0, 12)}...`));
  p.log.info(pc.dim("Type /help for commands, Ctrl+C to quit."));
}

/** Discriminated outcome of handling a single line of REPL input. */
type ReplInputAction = "quit" | "continue" | "send";

/**
 * Handle a trimmed line of REPL input that is a slash command (or empty).
 *
 * Returns `"quit"` to terminate the loop, `"continue"` to skip to the next prompt,
 * or `"send"` to forward the line to the agent. `setShowLog` toggles verbose output
 * for the `/log` command.
 */
function handleReplInput(
  trimmed: string,
  showLog: boolean,
  setShowLog: (v: boolean) => void,
): ReplInputAction {
  if (!trimmed) return "continue";

  if (trimmed === "/quit" || trimmed === "/q") {
    return "quit";
  }
  if (trimmed === "/help" || trimmed === "/h" || trimmed === "/?") {
    p.log.info(
      [
        "  /help, /h   — this help",
        "  /log        — toggle verbose log output",
        "  /quit, /q   — exit",
        "  Ctrl+C      — exit",
      ].join("\n"),
    );
    return "continue";
  }
  if (trimmed === "/log") {
    const next = !showLog;
    setShowLog(next);
    p.log.info(`Log output: ${next ? "on" : "off"}`);
    return "continue";
  }

  return "send";
}

/**
 * Start the scrolling REPL for an interactive agent conversation.
 *
 * Initialises the agent session (including OMP extension injection when the driver is `omp`),
 * prints a banner with project metadata, then enters a `@clack/prompts` input loop.
 * Each iteration awaits user input, forwards it to the agent via {@link createReplSession},
 * and blocks until the agent signals `idle` or `stopped`. Slash commands (`/help`, `/log`,
 * `/quit`) are handled before the message reaches the agent.
 *
 * @param opts - Configuration for the REPL session.
 * @returns Resolves when the session ends (user quit or fatal error).
 *
 * @docLink packages/tui/concepts#start-repl
 */
export async function startRepl(opts: ReplOptions): Promise<void> {
  const projectDir = resolve(opts.projectDir);
  const agentCwd = opts.cwd ? resolve(opts.cwd) : projectDir;

  // ── OMP workspace extension injection ──────────────────────────────
  const settings = await resolveSettings(projectDir, { driver: opts.driver });
  const resolvedDriver = settings.driver ?? "omp";
  const ompCleanup = await injectOmpExtension(resolvedDriver, projectDir, agentCwd);

  // ── Session resume ─────────────────────────────────────────────────
  const resumeSessionId = resolveResumeSessionId(projectDir, opts);

  // ── Telemetry ──────────────────────────────────────────────────────
  const telemetryBuffer: TelemetryLogEntry[] = [];
  const telemetry = new TuiTelemetryProvider((entry) => {
    telemetryBuffer.push(entry);
  });
  const trace = telemetry.startTrace({ name: "skaile-repl", kind: "chat_session" });

  // ── Create agent session ───────────────────────────────────────────
  const agentDir = resolveAgentDir(projectDir) ?? undefined;
  const session = await createAgentSession({
    projectDir,
    cwd: agentCwd !== projectDir ? agentCwd : undefined,
    agentDir,
    driver: opts.driver ?? resolvedDriver,
    model: opts.model,
    provider: opts.provider,
    resumeSessionId,
    telemetry,
    trace,
    onLog: () => {},
  });

  await session.driver.start();

  // ── Output + state tracking ────────────────────────────────────────
  let showLog = opts.verbose ?? false;
  let idleResolve: (() => void) | undefined;

  const write = (line: string) => {
    p.log.message(line);
  };

  const writeLog = (line: string) => {
    if (showLog) p.log.step(line);
  };

  // ── Create REPL session ────────────────────────────────────────────
  const replSession = createReplSession({
    session,
    onLine: write,
    onLog: writeLog,
    onStateChange: (state) => {
      if (state === "idle" || state === "stopped") {
        idleResolve?.();
      }
    },
  });

  // Drain buffered telemetry
  const sink = createTelemetrySink(writeLog);
  for (const entry of telemetryBuffer) sink(entry);
  telemetryBuffer.length = 0;

  // ── Banner ─────────────────────────────────────────────────────────
  printBanner({
    projectDir,
    displayDriver: session.driverType ?? resolvedDriver,
    displayModel: session.model ?? opts.model ?? "default",
    agentDir,
    resumeSessionId,
  });

  // ── REPL loop ──────────────────────────────────────────────────────
  function shutdown() {
    replSession.stop();
    telemetry.endTrace(trace, { status: "ok" });
    void telemetry.shutdown();
    ompCleanup?.();
    p.outro("Bye.");
    process.exit(0);
  }

  process.on("SIGINT", shutdown);
  process.on("SIGTERM", shutdown);

  while (replSession.state !== "stopped") {
    const input = await p.text({
      message: pc.gray(">>>"),
      placeholder: "",
    });

    if (p.isCancel(input)) {
      shutdown();
      return;
    }

    const trimmed = (input as string).trim();
    const action = handleReplInput(trimmed, showLog, (v) => {
      showLog = v;
    });
    if (action === "quit") {
      shutdown();
      return;
    }
    if (action === "continue") continue;

    const idlePromise = new Promise<void>((resolve) => {
      idleResolve = resolve;
    });
    replSession.send(trimmed);
    await idlePromise;
  }

  shutdown();
}
